/**
 * Scroll-reveal (animate on scroll) framework.
 *
 * Fades + slides elements in (opacity 0 → 1, translateY 20px → 0, 500ms ease-out)
 * as they enter the viewport, once per element. Targets:
 * - every `.shopify-section` wrapper inside `<main>` (revealed as a unit),
 * - any element with `[data-scroll-reveal]`,
 * - direct children of `[data-scroll-reveal-group]` containers, staggered by 80ms each.
 *
 * No-flash / no-LCP-damage guarantees:
 * - The hidden state (`.scroll-reveal`, see base.css) is gated on `html.scroll-reveal-ready`,
 *   which is only added here — if this module never runs, nothing is ever hidden.
 * - Elements already intersecting the viewport at init time (or added there later by the
 *   Section Rendering API / morph) are left fully visible, with no animation.
 * - With `prefers-reduced-motion: reduce`, init is skipped entirely — everything stays visible.
 *
 * Self-boots on import. Pairs with the `.scroll-reveal` styles in base.css.
 */
import { prefersReducedMotion } from '@theme/utilities';

const READY_CLASS = 'scroll-reveal-ready';
const HIDDEN_CLASS = 'scroll-reveal';
const REVEALED_CLASS = 'scroll-revealed';
const TARGET_SELECTOR = 'main .shopify-section, [data-scroll-reveal], [data-scroll-reveal-group] > *';
const STAGGER_MS = 80;
const STAGGER_CAP = 8;

/**
 * Elements already handled (hidden + observed, or revealed instantly).
 * Keyed on the node itself, so elements morphed in place are not re-processed.
 * @type {WeakSet<Element>}
 */
const processed = new WeakSet();

/** @type {IntersectionObserver | null} */
let intersectionObserver = null;

/**
 * Whether an element currently intersects the visual viewport.
 *
 * On desktop (≥990px) the scroll container is `.page-wrapper` (see scroll-container.js),
 * but it fills the viewport and clips its overflow, so a window-based check — and the
 * IntersectionObserver's default (viewport) root — remain correct on both layouts.
 *
 * @param {Element} element
 * @returns {boolean}
 */
function isInViewport(element) {
  const rect = element.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
}

/**
 * Applies the stagger delay when the element is a direct child of a reveal group.
 * The delay is capped so late items in long lists never wait multiple seconds.
 * @param {HTMLElement} element
 */
function applyStagger(element) {
  const parent = element.parentElement;
  if (!parent?.hasAttribute('data-scroll-reveal-group')) return;

  const index = Math.min([...parent.children].indexOf(element), STAGGER_CAP);
  if (index > 0) element.style.setProperty('--reveal-delay', `${index * STAGGER_MS}ms`);
}

/**
 * Finds and registers every reveal target inside (and including) a root:
 * targets already on screen are left untouched (LCP / morph-flicker safety),
 * the rest are hidden and observed.
 *
 * Measurement and mutation run in separate passes so a class write never
 * invalidates the layout the next measurement reads (layout thrash).
 * @param {Element | Document} root
 */
function processTree(root) {
  const candidates = [];
  if (root instanceof Element && root.matches(TARGET_SELECTOR)) candidates.push(root);
  candidates.push(...root.querySelectorAll(TARGET_SELECTOR));

  const belowFold = [];
  for (const element of candidates) {
    if (!(element instanceof HTMLElement) || processed.has(element)) continue;
    processed.add(element);
    if (!isInViewport(element)) belowFold.push(element);
  }

  for (const element of belowFold) {
    applyStagger(element);
    element.classList.add(HIDDEN_CLASS);
    intersectionObserver?.observe(element);
  }
}

/**
 * Reveals elements as they scroll into view, once each.
 * @param {IntersectionObserverEntry[]} entries
 */
function onIntersection(entries) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    reveal(entry.target);
  }
}

/**
 * Plays the entrance transition, then strips the reveal classes once it ends.
 *
 * Clearing the classes returns the element to its natural state — crucially
 * removing the residual `transform`, which would otherwise linger forever and
 * make the element a containing block that breaks `position: sticky`/`fixed`
 * descendants (e.g. the sticky add-to-cart bar inside a revealed section).
 * @param {Element} target
 */
function reveal(target) {
  intersectionObserver?.unobserve(target);
  target.classList.add(REVEALED_CLASS);
  const finishProperty = target.classList.contains('fan-espresso-cta-wrapper') ? 'opacity' : 'transform';

  /** @param {TransitionEvent} event */
  const onEnd = (event) => {
    // transitionend bubbles — wait for this element's own reveal transition.
    // The espresso CTA intentionally fades without an ancestor transform so
    // WebKit keeps the image's rounded clipping while the section enters.
    if (event.target !== target || event.propertyName !== finishProperty) return;
    target.removeEventListener('transitionend', onEnd);
    target.classList.remove(HIDDEN_CLASS, REVEALED_CLASS);
    if (target instanceof HTMLElement) target.style.removeProperty('--reveal-delay');
  };
  target.addEventListener('transitionend', onEnd);
}

function init() {
  if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;

  // On back/forward navigation the browser restores the previous scroll position;
  // re-hiding sections would blank the restored viewport until they animate back
  // in. The content was already seen — skip revealing entirely.
  const navigation = performance.getEntriesByType?.('navigation')[0];
  if (navigation && /** @type {PerformanceNavigationTiming} */ (navigation).type === 'back_forward') return;

  intersectionObserver = new IntersectionObserver(onIntersection);

  // Gate the hidden state on <html> before any element is hidden.
  document.documentElement.classList.add(READY_CLASS);
  processTree(document);

  const main = document.querySelector('main');
  if (!main) return;

  // Content added later (Section Rendering API morphs, infinite scroll): new nodes
  // already in the viewport reveal instantly (no flicker); nodes below the fold
  // are hidden and revealed on scroll like the initial ones.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) processTree(node);
      }
    }
  }).observe(main, { childList: true, subtree: true });
}

init();
